import {
  MainPageDefaultIcon,
  MainPageMonoIcon,
  MainPageSerifIcon,
  PreviewBlogImg,
} from '@components/App/Modal/PublishModal/components/Preview/icons'
import { PublishContent, PublishFonts } from '@storeOld/reducers/publish'
import cx from 'classnames'
import type React from 'react'

import { THEME_DARK } from '@/constants/constants'
import { SkeletonSize } from '@/types'
import { defineStrictWhiteColor } from '../../../../utils'
import { PagePreview } from '../PagePreview/PagePreview'
import { PreviewBlock } from '../PreviewBlock/PreviewBlock'
import { PreviewSkeleton } from '../PreviewSkeleton/PreviewSkeleton'
import { OptionalDivider } from './components/OptionalDivider'
import css from './styles.module.css'

type Props = {
  contentType: PublishContent
  pagePreview: boolean
  fontType: PublishFonts
  themeByColor: 'light' | 'dark'
  bgColor: string
}

const FontIcon: React.FC<Pick<Props, 'fontType'>> = ({ fontType }) => {
  switch (fontType) {
    case PublishFonts.DEFAULT: {
      return <MainPageDefaultIcon />
    }
    case PublishFonts.MONO: {
      return <MainPageMonoIcon />
    }
    case PublishFonts.SERIF: {
      return <MainPageSerifIcon />
    }
    default:
      return null
  }
}

export const PreviewContent: React.FC<Props> = ({
  contentType,
  pagePreview,
  fontType,
  themeByColor,
  bgColor,
}) => {
  const hasSheet = contentType === PublishContent.SHEET
  const hasIslands = contentType === PublishContent.ISLANDS

  const themeIsDark = (hasSheet || hasIslands) && themeByColor === THEME_DARK
  const sheetDarkTheme = hasSheet && themeByColor === THEME_DARK

  const bgColorStyle = { backgroundColor: bgColor }

  const islandsBgColorStyle = themeIsDark ? bgColorStyle : {}
  const sheetColorStyle = sheetDarkTheme ? { color: bgColor } : {}
  const sheetBgColorStyle = sheetDarkTheme ? bgColorStyle : {}

  const bgWhite = defineStrictWhiteColor(bgColor)

  return (
    <div
      className={cx(css.previewContent, {
        [css.sheet]: hasSheet,
        [css.bgWhite]: bgWhite,
        [css.withoutPagePreview]: !pagePreview,
      })}
    >
      <div className={css.previewContentInner}>
        <div
          className={cx(css.previewTitle, {
            [css.islands]: hasIslands,
          })}
          style={sheetColorStyle}
        >
          <FontIcon fontType={fontType} />
        </div>

        <div>
          <OptionalDivider hasIslands={hasIslands} lightBg={sheetDarkTheme} />

          <PreviewBlock contentType={contentType} bgWhite={bgWhite}>
            {pagePreview && (
              <PreviewSkeleton
                size={SkeletonSize.XS}
                style={islandsBgColorStyle}
              />
            )}
            <PreviewSkeleton
              size={SkeletonSize.L}
              style={islandsBgColorStyle}
            />
            <PreviewSkeleton
              size={SkeletonSize.XM}
              style={islandsBgColorStyle}
            />
          </PreviewBlock>

          <OptionalDivider hasIslands={hasIslands} lightBg={sheetDarkTheme} />

          <PreviewBlock contentType={contentType} bgWhite={bgWhite}>
            {pagePreview && (
              <PreviewSkeleton
                size={SkeletonSize.S}
                style={islandsBgColorStyle}
              />
            )}
            <div
              className={cx(css.skeletonImgWrap, {
                [css.withoutPagePreview]: !pagePreview,
                [css.themeDark]: themeByColor === THEME_DARK,
                [css.whiteFallback]: themeIsDark,
              })}
            >
              <PreviewBlogImg />
            </div>
            <PreviewSkeleton
              size={SkeletonSize.L}
              style={islandsBgColorStyle}
            />
            <PreviewSkeleton
              size={SkeletonSize.XM}
              style={islandsBgColorStyle}
            />
          </PreviewBlock>

          <OptionalDivider hasIslands={hasIslands} lightBg={sheetDarkTheme} />

          <PreviewBlock contentType={contentType} bgWhite={bgWhite}>
            {pagePreview && (
              <PreviewSkeleton
                size={SkeletonSize.XS}
                style={islandsBgColorStyle}
              />
            )}

            <PreviewSkeleton
              size={SkeletonSize.XM}
              style={islandsBgColorStyle}
            />
            <PreviewSkeleton
              size={SkeletonSize.L}
              style={islandsBgColorStyle}
            />
            <PreviewSkeleton style={islandsBgColorStyle} />
          </PreviewBlock>

          {!pagePreview && (
            <>
              <OptionalDivider
                hasIslands={hasIslands}
                lightBg={sheetDarkTheme}
              />
              <PreviewBlock contentType={contentType} bgWhite={bgWhite}>
                <PreviewSkeleton
                  size={SkeletonSize.L}
                  style={islandsBgColorStyle}
                />
              </PreviewBlock>
            </>
          )}
        </div>
      </div>

      {pagePreview && (
        <PagePreview
          sheetDarkTheme={sheetDarkTheme}
          style={{ ...sheetColorStyle, ...sheetBgColorStyle }}
        />
      )}
    </div>
  )
}
